import _ from 'lodash';
import React, { useEffect, useState, useMemo } from 'react';
import type { StoryMediaPlatform } from 'types/story';
import { clientLog } from 'common/utils/helpers';
import { getCurrentMediaQueryKey, getMediaQueryBoundaries } from 'common/utils/story-media-query';
import type { Props } from './types';
import { Context } from './Context';

const log = clientLog.extend('ResponsiveProvider');

const setPlatformAttr = (platform: StoryMediaPlatform) => document.body.setAttribute('data-platform', platform);

const ResponsiveProvider: React.FC<Props> = ({ mediaQuery, children }) => {
	const [currentMediaQueryKey, setCurrentMediaQueryKey] = useState(getCurrentMediaQueryKey({ mediaQuery }));
	const [boundaries, setBoundaries] = useState(
		getMediaQueryBoundaries({ currentMediaQuery: currentMediaQueryKey, mediaQuery })
	);

	useEffect(() => {
		log(`Set MediaQuery (initial) — %c${currentMediaQueryKey}`, 'font-weight: bold');
		setPlatformAttr(currentMediaQueryKey);

		const resizeListener = _.debounce(() => {
			const nextMediaQueryKey = getCurrentMediaQueryKey({ mediaQuery });
			if (nextMediaQueryKey !== currentMediaQueryKey) {
				log(`Set MediaQuery (update) — %c${nextMediaQueryKey}`, 'font-weight: bold');
				setCurrentMediaQueryKey(nextMediaQueryKey);
				setBoundaries(getMediaQueryBoundaries({ currentMediaQuery: nextMediaQueryKey, mediaQuery }));
				setPlatformAttr(nextMediaQueryKey);
			}
		}, 300);
		window.addEventListener('resize', resizeListener);

		return () => {
			window.removeEventListener('resize', resizeListener);
		};
	}, [mediaQuery, currentMediaQueryKey]);

	const value = useMemo(
		() => ({ currentMediaQuery: currentMediaQueryKey, boundaries, mediaQuery }),
		[currentMediaQueryKey, boundaries, mediaQuery]
	);

	return <Context.Provider value={value}>{children}</Context.Provider>;
};

export default React.memo(ResponsiveProvider);
